// 搜索和筛选功能实现

import type {
  BookInfo,
  SearchQuery,
  SearchResult,
  SearchHistory,
  SearchSuggestion
} from '@/types/reading';

import type { StorageManager } from './storage';
import { BookSearchManager } from './bookSearchManager';
import { BookLibrary } from './bookLibrary';

export class SearchService {
  private storage: StorageManager;
  private searchManager: BookSearchManager;
  private bookLibrary: BookLibrary;
  private searchHistory: SearchHistory[] = [];
  private searchHistoryLoaded = false;
  private readonly MAX_HISTORY_SIZE = 50;
  private readonly MAX_SUGGESTIONS = 10;

  constructor(
    storage: StorageManager, 
    searchManager: BookSearchManager, 
    bookLibrary: BookLibrary
  ) {
    this.storage = storage;
    this.searchManager = searchManager;
    this.bookLibrary = bookLibrary;
    // Search history will be loaded lazily when needed
  }

  // ============================================================================
  // 全文搜索功能
  // ============================================================================

  async searchBooks(query: SearchQuery): Promise<SearchResult> {
    try {
      // 1. 记录搜索历史
      await this.addToSearchHistory(query.keyword);

      // 2. 执行搜索
      let result: SearchResult;

      if (query.searchScope === 'library') {
        // 在图书库中搜索
        result = await this.searchInLibrary(query);
      } else {
        // 在所有书源中搜索
        result = await this.searchManager.searchBooksAcrossSources(query);
      }

      // 3. 应用高级筛选
      if (this.hasAdvancedFilters(query)) {
        result.books = this.applyAdvancedFilters(result.books, query);
        result.total = result.books.length;
      }

      return result;

    } catch (error) {
      console.error('Search books failed:', error);
      return this.createEmptyResult(query);
    }
  }

  private async searchInLibrary(query: SearchQuery): Promise<SearchResult> {
    try {
      // 获取图书库中的所有图书
      const libraryBooks = await this.bookLibrary.getLibraryBooks();
      
      // 执行本地搜索
      const filteredBooks = this.performLocalSearch(libraryBooks, query);
      
      // 排序
      const sortedBooks = this.sortBooks(filteredBooks, query.sortBy || 'relevance', query.keyword);
      
      // 分页
      const startIndex = (query.page - 1) * query.pageSize;
      const endIndex = startIndex + query.pageSize;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      return {
        books: paginatedBooks,
        total: sortedBooks.length,
        page: query.page,
        pageSize: query.pageSize,
        suggestions: this.generateLocalSuggestions(query.keyword, libraryBooks),
        searchTime: Date.now()
      };

    } catch (error) {
      console.error('Search in library failed:', error);
      return this.createEmptyResult(query);
    }
  }

  private performLocalSearch(books: BookInfo[], query: SearchQuery): BookInfo[] {
    if (!query.keyword) {
      return books;
    }

    const keyword = query.keyword.toLowerCase();
    
    return books.filter(book => {
      // 标题匹配
      if (book.title.toLowerCase().includes(keyword)) {
        return true;
      }
      
      // 作者匹配
      if (book.author.toLowerCase().includes(keyword)) {
        return true;
      }
      
      // 描述匹配
      if (book.description && book.description.toLowerCase().includes(keyword)) {
        return true;
      }
      
      // 标签匹配
      if (book.tags.some(tag => tag.toLowerCase().includes(keyword))) {
        return true;
      }
      
      // 分类匹配
      if (book.category.toLowerCase().includes(keyword)) {
        return true;
      }
      
      return false;
    });
  }

  // ============================================================================
  // 高级筛选功能
  // ============================================================================

  private hasAdvancedFilters(query: SearchQuery): boolean {
    return !!(
      query.minRating ||
      query.maxRating ||
      query.minWordCount ||
      query.maxWordCount ||
      query.publishDateRange ||
      query.updateDateRange ||
      query.status ||
      query.language
    );
  }

  private applyAdvancedFilters(books: BookInfo[], query: SearchQuery): BookInfo[] {
    return books.filter(book => {
      // 评分筛选
      if (query.minRating && (book.rating || 0) < query.minRating) {
        return false;
      }
      if (query.maxRating && (book.rating || 0) > query.maxRating) {
        return false;
      }

      // 字数筛选
      if (query.minWordCount && (book.wordCount || 0) < query.minWordCount) {
        return false;
      }
      if (query.maxWordCount && (book.wordCount || 0) > query.maxWordCount) {
        return false;
      }

      // 发布日期筛选
      if (query.publishDateRange && book.publishDate) {
        const publishTime = book.publishDate.getTime();
        if (query.publishDateRange.start && publishTime < query.publishDateRange.start.getTime()) {
          return false;
        }
        if (query.publishDateRange.end && publishTime > query.publishDateRange.end.getTime()) {
          return false;
        }
      }

      // 更新日期筛选
      if (query.updateDateRange) {
        const updateTime = book.updateTime.getTime();
        if (query.updateDateRange.start && updateTime < query.updateDateRange.start.getTime()) {
          return false;
        }
        if (query.updateDateRange.end && updateTime > query.updateDateRange.end.getTime()) {
          return false;
        }
      }

      // 状态筛选
      if (query.status && book.status !== query.status) {
        return false;
      }

      // 语言筛选
      if (query.language && book.language !== query.language) {
        return false;
      }

      return true;
    });
  }

  // ============================================================================
  // 搜索建议功能
  // ============================================================================

  async getSearchSuggestions(keyword: string): Promise<SearchSuggestion[]> {
    try {
      const suggestions: SearchSuggestion[] = [];

      if (keyword.length < 2) {
        return suggestions;
      }

      // 1. 从搜索历史中获取建议
      const historySuggestions = await this.getHistorySuggestions(keyword);
      suggestions.push(...historySuggestions);

      // 2. 从图书库中获取建议
      const librarySuggestions = await this.getLibrarySuggestions(keyword);
      suggestions.push(...librarySuggestions);

      // 3. 去重和排序
      const uniqueSuggestions = this.deduplicateSuggestions(suggestions);
      
      return uniqueSuggestions.slice(0, this.MAX_SUGGESTIONS);

    } catch (error) {
      console.error('Get search suggestions failed:', error);
      return [];
    }
  }

  private async getHistorySuggestions(keyword: string): Promise<SearchSuggestion[]> {
    await this.loadSearchHistory();
    const keywordLower = keyword.toLowerCase();
    
    return this.searchHistory
      .filter(history => history.keyword.toLowerCase().includes(keywordLower))
      .map(history => ({
        text: history.keyword,
        type: 'history' as const,
        count: history.searchCount
      }))
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, 5);
  }

  private async getLibrarySuggestions(keyword: string): Promise<SearchSuggestion[]> {
    try {
      const libraryBooks = await this.bookLibrary.getLibraryBooks();
      const suggestions: SearchSuggestion[] = [];
      const keywordLower = keyword.toLowerCase();

      // 作者建议
      const authors = new Set<string>();
      libraryBooks.forEach(book => {
        if (book.author.toLowerCase().includes(keywordLower)) {
          authors.add(book.author);
        }
      });

      authors.forEach(author => {
        suggestions.push({
          text: author,
          type: 'author',
          count: libraryBooks.filter(book => book.author === author).length
        });
      });

      // 标签建议
      const tags = new Set<string>();
      libraryBooks.forEach(book => {
        book.tags.forEach(tag => {
          if (tag.toLowerCase().includes(keywordLower)) {
            tags.add(tag);
          }
        });
      });

      tags.forEach(tag => {
        suggestions.push({
          text: tag,
          type: 'tag',
          count: libraryBooks.filter(book => book.tags.includes(tag)).length
        });
      });

      // 分类建议
      const categories = new Set<string>();
      libraryBooks.forEach(book => {
        if (book.category.toLowerCase().includes(keywordLower)) {
          categories.add(book.category);
        }
      });

      categories.forEach(category => {
        suggestions.push({
          text: category,
          type: 'category',
          count: libraryBooks.filter(book => book.category === category).length
        });
      });

      return suggestions;

    } catch (error) {
      console.error('Get library suggestions failed:', error);
      return [];
    }
  }

  private deduplicateSuggestions(suggestions: SearchSuggestion[]): SearchSuggestion[] {
    const seen = new Set<string>();
    const unique: SearchSuggestion[] = [];

    for (const suggestion of suggestions) {
      const key = `${suggestion.text}_${suggestion.type}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(suggestion);
      }
    }

    // 按相关性和使用频率排序
    return unique.sort((a, b) => {
      // 历史记录优先
      if (a.type === 'history' && b.type !== 'history') return -1;
      if (b.type === 'history' && a.type !== 'history') return 1;
      
      // 按计数排序
      return (b.count || 0) - (a.count || 0);
    });
  }

  // ============================================================================
  // 搜索历史管理
  // ============================================================================

  private async loadSearchHistory(): Promise<void> {
    if (this.searchHistoryLoaded) {
      return;
    }
    
    try {
      const history = await this.storage.getSearchHistory();
      this.searchHistory = history || [];
      this.searchHistoryLoaded = true;
    } catch (error) {
      console.error('Load search history failed:', error);
      this.searchHistory = [];
      this.searchHistoryLoaded = true; // Mark as loaded even on error to prevent retry loops
    }
  }

  private async addToSearchHistory(keyword: string): Promise<void> {
    await this.loadSearchHistory();
    try {
      if (!keyword || keyword.trim().length === 0) {
        return;
      }

      const trimmedKeyword = keyword.trim();
      
      // 查找现有记录
      const existingIndex = this.searchHistory.findIndex(
        history => history.keyword === trimmedKeyword
      );

      if (existingIndex >= 0) {
        // 更新现有记录
        const existing = this.searchHistory[existingIndex];
        existing.searchCount = (existing.searchCount || 0) + 1;
        existing.lastSearchTime = new Date();
        
        // 移到最前面
        this.searchHistory.splice(existingIndex, 1);
        this.searchHistory.unshift(existing);
      } else {
        // 添加新记录
        const newHistory: SearchHistory = {
          id: this.generateHistoryId(),
          keyword: trimmedKeyword,
          timestamp: new Date(),
          resultCount: 0,
          searchCount: 1,
          lastSearchTime: new Date()
        };
        
        this.searchHistory.unshift(newHistory);
      }

      // 限制历史记录数量
      if (this.searchHistory.length > this.MAX_HISTORY_SIZE) {
        this.searchHistory = this.searchHistory.slice(0, this.MAX_HISTORY_SIZE);
      }

      // 保存到存储
      await this.storage.saveSearchHistory(this.searchHistory);

    } catch (error) {
      console.error('Add to search history failed:', error);
    }
  }

  async getSearchHistory(): Promise<SearchHistory[]> {
    await this.loadSearchHistory();
    return [...this.searchHistory];
  }

  async clearSearchHistory(): Promise<void> {
    await this.loadSearchHistory();
    try {
      this.searchHistory = [];
      await this.storage.saveSearchHistory([]);
    } catch (error) {
      console.error('Clear search history failed:', error);
    }
  }

  async removeFromSearchHistory(keyword: string): Promise<void> {
    await this.loadSearchHistory();
    try {
      this.searchHistory = this.searchHistory.filter(
        history => history.keyword !== keyword
      );
      await this.storage.saveSearchHistory(this.searchHistory);
    } catch (error) {
      console.error('Remove from search history failed:', error);
    }
  }

  // ============================================================================
  // 排序和分页
  // ============================================================================

  private sortBooks(books: BookInfo[], sortBy: string, keyword?: string): BookInfo[] {
    switch (sortBy) {
      case 'rating':
        return books.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      
      case 'updateTime':
        return books.sort((a, b) => b.updateTime.getTime() - a.updateTime.getTime());
      
      case 'publishDate':
        return books.sort((a, b) => {
          const aTime = a.publishDate?.getTime() || 0;
          const bTime = b.publishDate?.getTime() || 0;
          return bTime - aTime;
        });
      
      case 'wordCount':
        return books.sort((a, b) => (b.wordCount || 0) - (a.wordCount || 0));
      
      case 'title':
        return books.sort((a, b) => a.title.localeCompare(b.title));
      
      case 'author':
        return books.sort((a, b) => a.author.localeCompare(b.author));
      
      case 'popularity':
        return books.sort((a, b) => {
          const scoreA = this.calculatePopularityScore(a);
          const scoreB = this.calculatePopularityScore(b);
          return scoreB - scoreA;
        });
      
      default: // relevance
        if (keyword) {
          return books.sort((a, b) => {
            const scoreA = this.calculateRelevanceScore(a, keyword);
            const scoreB = this.calculateRelevanceScore(b, keyword);
            return scoreB - scoreA;
          });
        }
        return books;
    }
  }

  private calculatePopularityScore(book: BookInfo): number {
    let score = 0;
    
    // 评分权重
    if (book.rating) {
      score += book.rating * 20;
    }
    
    // 字数权重
    if (book.wordCount) {
      score += Math.log(book.wordCount / 1000) * 5;
    }
    
    // 章节数权重
    score += book.chapters.length * 0.1;
    
    // 更新时间权重
    const daysSinceUpdate = (Date.now() - book.updateTime.getTime()) / (24 * 60 * 60 * 1000);
    score += Math.max(0, 30 - daysSinceUpdate) * 0.5;
    
    return score;
  }

  private calculateRelevanceScore(book: BookInfo, keyword: string): number {
    const keywordLower = keyword.toLowerCase();
    let score = 0;
    
    // 标题匹配
    if (book.title.toLowerCase().includes(keywordLower)) {
      score += 10;
      if (book.title.toLowerCase().startsWith(keywordLower)) {
        score += 5;
      }
    }
    
    // 作者匹配
    if (book.author.toLowerCase().includes(keywordLower)) {
      score += 5;
    }
    
    // 描述匹配
    if (book.description && book.description.toLowerCase().includes(keywordLower)) {
      score += 2;
    }
    
    // 标签匹配
    book.tags.forEach(tag => {
      if (tag.toLowerCase().includes(keywordLower)) {
        score += 1;
      }
    });
    
    return score;
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private generateLocalSuggestions(keyword: string, books: BookInfo[]): string[] {
    const suggestions = new Set<string>();
    const keywordLower = keyword.toLowerCase();

    books.forEach(book => {
      // 作者建议
      if (book.author && !book.author.toLowerCase().includes(keywordLower)) {
        suggestions.add(book.author);
      }
      
      // 标签建议
      book.tags.forEach(tag => {
        if (!tag.toLowerCase().includes(keywordLower)) {
          suggestions.add(tag);
        }
      });
    });

    return Array.from(suggestions).slice(0, 5);
  }

  private generateHistoryId(): string {
    return `history_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private createEmptyResult(query: SearchQuery): SearchResult {
    return {
      books: [],
      total: 0,
      page: query.page,
      pageSize: query.pageSize,
      suggestions: [],
      searchTime: Date.now()
    };
  }

  // ============================================================================
  // 搜索统计
  // ============================================================================

  async getSearchStats(): Promise<{
    totalSearches: number;
    uniqueKeywords: number;
    topKeywords: Array<{ keyword: string; count: number }>;
    recentSearches: SearchHistory[];
  }> {
    await this.loadSearchHistory();
    try {
      const totalSearches = this.searchHistory.reduce(
        (sum, history) => sum + (history.searchCount || 0), 
        0
      );

      const topKeywords = this.searchHistory
        .sort((a, b) => (b.searchCount || 0) - (a.searchCount || 0))
        .slice(0, 10)
        .map(history => ({
          keyword: history.keyword,
          count: history.searchCount || 0
        }));

      const recentSearches = this.searchHistory
        .sort((a, b) => b.lastSearchTime.getTime() - a.lastSearchTime.getTime())
        .slice(0, 10);

      return {
        totalSearches,
        uniqueKeywords: this.searchHistory.length,
        topKeywords,
        recentSearches
      };

    } catch (error) {
      console.error('Get search stats failed:', error);
      return {
        totalSearches: 0,
        uniqueKeywords: 0,
        topKeywords: [],
        recentSearches: []
      };
    }
  }
}